// >>> **ITEM G2, THE 12TH HOUSE STORE APP (her box, 2026-10-13).** <<< ONE codebase: the website and
// the store app are the same React front end, and `npm run build:app` (vite --mode app) is the only
// thing that switches this on. In the website build APP_MODE is false and every function here is a
// no-op, so the website cannot change because the app exists.
//
// CLIENT-ONLY PURE LAW: imports nothing, so a .mjs gate can load it. `import.meta.env` is undefined
// under plain node, hence the guarded read.

type Env = { VITE_APP_MODE?: string; VITE_APP_API_BASE?: string };
const env: Env = ((import.meta as unknown as { env?: Env }).env) || {};

// `__APP_MODE__` is written in by vite.config.ts as a literal, so the website build folds every
// `if (APP_MODE)` branch away entirely. Under plain node (a gate loading this file) it does not exist.
declare const __APP_MODE__: boolean | undefined;
export const APP_MODE: boolean = typeof __APP_MODE__ !== "undefined" && __APP_MODE__ === true;

// The server the app talks to. Staging first (her box), production once she says so; the build sets it.
export const APP_API_BASE: string = String(env.VITE_APP_API_BASE || "").replace(/\/+$/, "");

// The app's pages live inside the phone, so a bare "/api/..." would ask the phone. Every server path
// in this front end starts "/api/" (measured 2026-10-13: 492 literals, no other server prefix), so
// that is the one thing pointed at the server.
export function toServer(url: string, base: string = APP_API_BASE): string {
  if (!base || typeof url !== "string") return url;
  return url.startsWith("/api/") || url === "/api" ? base + url : url;
}

// R2: the ONE website link the app may show, the sign-up page, on the same house the app talks to.
export const APP_SIGNUP_URL: string = APP_API_BASE ? `${APP_API_BASE}/signup` : "/signup";

// R3: nothing is bought inside the app. Even if a purchase control slipped past the swaps, these
// requests are answered inside the phone and never reach the server's checkout or billing doors.
export const APP_REFUSED_PATHS: readonly string[] = ["/api/stripe/checkout", "/api/membership/portal"];
export function appRefuses(url: string): boolean {
  return APP_REFUSED_PATHS.some((p) => url === p || url.startsWith(`${p}?`) || url.startsWith(`${p}/`));
}

const URL_ATTRS = ["src", "href", "poster", "srcset", "style"];

/**
 * ITEM K2 (her TestFlight test, 2026-10-13 night: "the images to customize my room... have disappeared and so is the
 * background of my room"). PROVEN CAUSE: a picture can name the server in two more ways than src/href/poster, and
 * the app re-pointed none of them, so the phone asked ITSELF for /api/assets/room/... and drew nothing:
 *   srcset   the phone-size room pictures (<picture><source srcSet="/api/assets/room/<slug>-v">), chosen on a phone;
 *   style    inline backgrounds, url("/api/...") (the closet thumbnails, generator backdrops, shared post art).
 * Returns the value re-pointed at the server, or null when nothing in it names /api/.
 */
export function serverAttr(attr: string, value: string, base: string = APP_API_BASE): string | null {
  if (!base || !value || !value.includes("/api/")) return null;
  let next = value;
  if (attr === "srcset") next = value.replace(/(^|,)(\s*)\/api\//g, `$1$2${base}/api/`);
  else if (attr === "style") next = value.replace(/url\((\s*)(["']?)\/api\//g, `url($1$2${base}/api/`);
  else if (value.startsWith("/api/")) next = base + value;
  return next === value ? null : next;
}

type Win = {
  fetch: typeof fetch;
  XMLHttpRequest: typeof XMLHttpRequest;
  MutationObserver: typeof MutationObserver;
  document: Document;
  Request: typeof Request;
};

// Installed once at boot in the app build. fetch and XHR carry the cookie across (credentials
// "include" is the app's equivalent of the website's same-origin default); an <img>, <video>, <audio>
// or <a> written with "/api/..." is re-pointed the moment it lands in the page.
export function installServerBase(win: Win, base: string = APP_API_BASE): boolean {
  if (!base) return false;
  const nativeFetch = win.fetch.bind(win);
  win.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
    if (typeof input === "string") {
      if (appRefuses(input)) {
        return Promise.resolve(new Response(JSON.stringify({ message: "Not included in your plan" }), { status: 403, headers: { "Content-Type": "application/json" } }));
      }
      return nativeFetch(toServer(input, base), { credentials: "include", ...init });
    }
    return nativeFetch(input, init);
  }) as typeof fetch;

  const open = win.XMLHttpRequest.prototype.open;
  win.XMLHttpRequest.prototype.open = function patchedOpen(this: XMLHttpRequest, method: string, url: string | URL, ...rest: unknown[]) {
    this.withCredentials = true;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (open as any).call(this, method, typeof url === "string" ? toServer(url, base) : url, ...rest);
  } as typeof open;

  const fix = (el: Element) => {
    for (const attr of URL_ATTRS) {
      const next = serverAttr(attr, el.getAttribute(attr) || "", base);
      if (next !== null) el.setAttribute(attr, next);
    }
  };
  const sweep = (root: Element) => {
    fix(root);
    root.querySelectorAll("[src^='/api/'],[href^='/api/'],[poster^='/api/'],[srcset*='/api/'],[style*='/api/']").forEach(fix);
  };
  new win.MutationObserver((records) => {
    for (const r of records) {
      if (r.type === "attributes" && r.target instanceof Element) fix(r.target);
      r.addedNodes.forEach((n) => { if (n instanceof Element) sweep(n); });
    }
  }).observe(win.document.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: URL_ATTRS });
  sweep(win.document.documentElement);
  return true;
}
